/**
 * UserProfile - a chore poster's public page
 * ------------------------------------------
 * What a student looks at before deciding whether to apply: how many chores
 * this person has actually seen through, and what other students said.
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, CheckCircle2 } from 'lucide-react';

import { userApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { money, dateOnly, dateTime } from '../utils/format';
import Avatar from '../components/Avatar';
import StarRating from '../components/StarRating';
import ReviewCard from '../components/ReviewCard';
import ReportButton from '../components/ReportButton';
import BackLink from '../components/BackLink';
import Badge from '../components/Badge';
import Loader from '../components/Loader';
import { useI18n } from '../hooks/useI18n';

export default function UserProfile() {
  const { t } = useI18n();
  const { id } = useParams();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    userApi
      .get(id)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loader label={t('profile.loading')} />;

  if (!data) {
    return (
      <div className="container section">
        <div className="alert alert-error">{error || t('profile.posterNotFound')}</div>
      </div>
    );
  }

  const { user: poster, reviews, openChores } = data;
  const isMe = user && String(user._id) === String(poster._id);

  return (
    <>
      <div className="profile-banner">
        <div className="container">
          <BackLink
            to="/marketplace"
            label={t('choreDetail.backToMarketplace')}
            style={{ color: 'var(--color-white)', opacity: 0.95 }}
          />
          <div className="profile-card">
            <Avatar person={poster} size="lg" />

            <div className="profile-card-body">
              <h1>
                {poster.firstName} {poster.lastName}
              </h1>

              {/* See the note by the same two classes in StudentProfile.jsx. */}
              <div className="row profile-rating">
                <StarRating
                  value={poster.rating?.average || 0}
                  count={poster.rating?.count || 0}
                  size={18}
                />
              </div>

              <div className="row profile-meta">
                {poster.location?.city && (
                  <span className="row" style={{ gap: 5 }}>
                    <MapPin size={15} aria-hidden="true" />
                    {poster.location.city}
                  </span>
                )}
                <span className="row" style={{ gap: 5 }}>
                  <CheckCircle2 size={15} aria-hidden="true" />
                  {poster.choresCompleted} chore{poster.choresCompleted === 1 ? '' : 's'} completed
                  by students
                </span>
                <span>Member since {dateOnly(poster.createdAt)}</span>
              </div>
            </div>

            {isMe ? (
              <Link to="/settings" className="btn btn-inline" style={{ background: 'white', color: 'var(--color-blue)' }}>
                {t('profile.editMyProfile')}
              </Link>
            ) : (
              <ReportButton aboutUser={poster._id} personName={poster.firstName} />
            )}
          </div>
        </div>
      </div>

      <div className="container section">
        <div className="sidebar-layout">
          <div className="card">
            <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('profile.atAGlance')}</h3>
            <div className="stack" style={{ gap: 'var(--space-xs)' }}>
              <div className="row-between">
                {/* "Chores completed" alone read as though the poster did the
                    work themselves - they did not, a student did. */}
                <span className="text-sm">{t('profile.choresCompletedByStudents')}</span>
                <strong>{poster.choresCompleted}</strong>
              </div>
              <div className="row-between">
                <span className="text-sm">{t('profile.openRightNow')}</span>
                <strong>{openChores.length}</strong>
              </div>
              <div className="row-between">
                <span className="text-sm">{t('profile.reviewsFromStudents')}</span>
                <strong>{poster.rating?.count || 0}</strong>
              </div>
            </div>
          </div>

          <div className="stack" style={{ gap: 'var(--space-md)' }}>
            {openChores.length > 0 && (
              <div className="card">
                <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('profile.choresOpenNow')}</h3>
                <div className="stack" style={{ gap: 0 }}>
                  {openChores.map((chore) => (
                    <div className="detail-row" key={chore._id}>
                      <span style={{ flex: 1 }}>
                        <Link to={`/chores/${chore._id}`}>{chore.title}</Link>
                        <div className="text-sm text-muted">
                          {dateTime(chore.scheduledFor)} · {chore.location?.city}
                        </div>
                      </span>
                      <div className="row" style={{ gap: 'var(--space-xs)' }}>
                        <Badge tone="soft-blue">{chore.category}</Badge>
                        <span className="pay-amount">{money(chore.pay)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="card">
              <h3 style={{ marginBottom: 'var(--space-sm)' }}>
                What students say ({poster.rating?.count || 0})
              </h3>

              {reviews.length === 0 ? (
                <p className="text-muted">{t('profile.noReviews')}</p>
              ) : (
                reviews.map((review) => <ReviewCard key={review._id} review={review} />)
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
